import { ChangeDetectionStrategy, Component, computed, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { AccessLevel, Marketplace } from "@front/m19-api-client";
import { AccountGroup } from "@front/m19-models";
import { AccountSelectionService } from "@front/m19-services";
import { IBadgeComponent } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { AccountGroupSelectorComponent } from "@m19-board/account-group-selector/account-group-selector.component";
import { MarketplaceSelectorComponent } from "@m19-board/marketplace-selector/marketplace-selector.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { map } from "rxjs";

@Component({
  selector: "app-multi-account-selector",
  template: ` <div *transloco="let t" class="flex items-center gap-2">
    <app-account-group-selector
      [multiSelection]="true"
      [selectedAccountGroupIds]="selectedAccountGroupIds()"
      (accountGroupsChange)="selectAccountGroups($event)"
      class="sensitive-data max-w-72"
    />
    <app-marketplace-selector
      class="sensitive-data"
      [marketplaces]="marketplaces()"
      [selectedMarketplaces]="selectedMarketplaces() ?? []"
      [multiSelection]="true"
      (marketplacesChange)="selectMarketplace($event)"
    />
    @if (isReadOnly()) {
      <IBadge
        [tooltipValue]="t('account-selector.read_only_mode')"
        color="gray"
        size="xs"
        icon="eye"
        [label]="t('account-selector.read_only')"
        class="ml-2"
      />
    }
  </div>`,
  imports: [TranslocoDirective, AccountGroupSelectorComponent, MarketplaceSelectorComponent, IBadgeComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class MultiAccountSelectorComponent {
  private accountSelectionService = inject(AccountSelectionService);
  private router = inject(Router);
  private translocoService = inject(TranslocoService);
  readonly isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$, { requireSync: true });

  static readonly MaxSelectedMarketplaceDisplayed = 3;
  readonly selectedAccountGroups = toSignal(
    this.accountSelectionService.multiAccountSelection$.pipe(
      map((accountGroups) => accountGroups.map((ag) => ag.accountGroup)),
    ),
    {
      requireSync: true,
    },
  );

  readonly selectedMarketplaces = toSignal(
    this.accountSelectionService.multiAccountSelection$.pipe(
      map((accountGroups) => accountGroups.map((ag) => ag.marketplaces).flat()),
    ),
  );

  readonly selectedAccountGroupIds = computed(() => this.selectedAccountGroups().map((a) => a.id));

  readonly marketplaces = computed<Marketplace[]>(() => {
    return Array.from(
      new Set(
        this.selectedAccountGroups()
          .flatMap((a) => a.resources)
          .map((r) => r.marketplace)
          .sort(),
      ),
    );
  });

  constructor() {
    // TODO: we should move this to a guard in the router configuration
    // route to dashboard 360 if stats only access level on one of the selected account/marketplace
    this.accountSelectionService.multiAccountSelection$.pipe(untilDestroyed(this)).subscribe((selection) => {
      const statsOnlySelected = selection.flatMap((a) =>
        a.accountGroup
          .getAccountMarketplaces()
          .filter((r) => a.marketplaces.includes(r.marketplace) && r.accessLevel === AccessLevel.STATS_ONLY),
      );
      if (statsOnlySelected.length > 0) {
        this.router.navigate(["/dashboard360"], {
          queryParams: {
            accountId: statsOnlySelected[0].accountId,
            marketplace: statsOnlySelected[0].marketplace,
            orgId: statsOnlySelected[0].resourceOrganizationId,
          },
          queryParamsHandling: "merge",
        });
      }
    });
  }

  selectAccountGroups(accountGroups: AccountGroup[]) {
    this.accountSelectionService.selectAccountGroups(accountGroups);
  }

  selectMarketplace(marketplaces: Marketplace[]) {
    this.accountSelectionService.setAccountSelection(
      this.selectedAccountGroups().map((a) => ({
        accountGroup: a,
        marketplaces: marketplaces.filter((m) => a.resources.map((r) => r.marketplace).includes(m)),
        accountMarketplaces: a.getAccountMarketplaces().filter((am) => marketplaces.includes(am.marketplace)),
      })),
    );
    if (this.selectedAccountGroups().length === 0) {
      return;
    }
    // TODO: we should move this to a guard in the router configuration
    // route to dashboard 360 if stats only access level on one of the selected account/marketplace
    const statsOnlySelected = this.selectedAccountGroups()
      .flatMap((a) => a.resources)
      .find((a) => a.accessLevel === AccessLevel.STATS_ONLY);
    if (statsOnlySelected) {
      this.router.navigate(["/dashboard360"], {
        queryParams: {
          accountId: statsOnlySelected.accountId,
          marketplace: statsOnlySelected.marketplace,
          orgId: statsOnlySelected.resourceOrganizationId,
        },
        queryParamsHandling: "merge",
      });
      return;
    }
  }
}
